import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from "@lexical/react/LexicalTypeaheadMenuPlugin"
import { $createHeadingNode } from "@lexical/rich-text"
import { $setBlocksType } from "@lexical/selection"
import { INSERT_TABLE_COMMAND } from "@lexical/table"
import { $getSelection, $isRangeSelection, TextNode } from "lexical"
import {
  CodeIcon,
  Heading1Icon,
  Heading2Icon,
  Heading3Icon,
  ImageIcon,
  ListIcon,
  ListOrderedIcon,
  ListTodoIcon,
  TableIcon,
} from "lucide-react"
import { createPortal } from "react-dom"

import { OPEN_IMAGE_DIALOG_COMMAND } from "@/components/lexical/plugins/inline-image"
import { OPEN_RAW_HTML_DIALOG_COMMAND } from "@/components/lexical/plugins/raw-html"

class SlashCommandOption extends MenuOption {
  title: string
  keywords: Array<string>
  icon: React.JSX.Element
  onSelect: (queryString: string) => void

  constructor(
    title: string,
    {
      keywords = [],
      icon,
      onSelect,
    }: {
      keywords?: Array<string>
      icon: React.JSX.Element
      onSelect: (queryString: string) => void
    }
  ) {
    super(title)
    this.title = title
    this.keywords = keywords
    this.icon = icon
    this.onSelect = onSelect
  }
}

function SlashCommandMenuItem({
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
  option: SlashCommandOption
}): React.JSX.Element {
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={`text-tertiary-foreground flex cursor-pointer items-center px-3 py-1.5 text-sm ${
        isSelected ? "bg-secondary" : ""
      }`}
      role="option"
      aria-selected={isSelected}
      ref={option.setRefElement}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <span className="mr-2 flex w-3">{option.icon}</span>
      <span className="leading-tight">{option.title}</span>
    </li>
  )
}

export default function SlashCommandPlugin({
  disableHeadings,
  enableHtml = false,
  enableImages = true,
  enableTable = false,
  enableChecklist = false,
}: {
  disableHeadings?: boolean
  enableHtml?: boolean
  enableImages?: boolean
  enableTable?: boolean
  enableChecklist?: boolean
}): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  const [query, setQuery] = useState<string | null>("")
  const [menuPortal, setMenuPortal] = useState<HTMLElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const triggerMatch = useBasicTypeaheadTriggerMatch("/", { minLength: 0 })

  useEffect(() => {
    const menuElement = document.createElement("div")
    menuElement.className = "slash-command-menu-portal"
    Object.assign(menuElement.style, {
      position: "fixed",
      left: "0px",
      top: "0px",
      zIndex: "9999",
    })
    document.body.appendChild(menuElement)
    setMenuPortal(menuElement)

    return () => {
      if (document.body.contains(menuElement)) {
        document.body.removeChild(menuElement)
      }
    }
  }, [])

  const positionMenu = useCallback(() => {
    const portalEl = menuRef.current
    if (!portalEl) return

    // Position below the caret line. Reading the selection's DOM range directly
    // bypasses Lexical's typeahead anchor, whose offsetHeight is 0 because we portal
    // the menu out of the containerDiv — so its rect.top sits on the caret line
    // and would overlap the text being typed.
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0) return
    const rect = selection.getRangeAt(0).getBoundingClientRect()
    if (rect.width === 0 && rect.height === 0) return

    const top = Math.round(rect.bottom + 4)
    const left = Math.round(rect.left)

    portalEl.style.transform = `translate(${left}px, ${top}px)`
  }, [])

  // Reposition menu on scroll/resize so it follows the caret
  useEffect(() => {
    const handleReposition = () => positionMenu()
    window.addEventListener("scroll", handleReposition, true)
    window.addEventListener("resize", handleReposition)
    return () => {
      window.removeEventListener("scroll", handleReposition, true)
      window.removeEventListener("resize", handleReposition)
    }
  }, [positionMenu])

  const options = useMemo(() => {
    const lower = (query ?? "").toLowerCase()

    // Heading options
    const headingOptions: SlashCommandOption[] = [
      new SlashCommandOption("Heading 1", {
        icon: <Heading1Icon />,
        keywords: ["h1", "heading1"],
        onSelect: () => {
          editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode("h1"))
            }
          })
        },
      }),
      new SlashCommandOption("Heading 2", {
        icon: <Heading2Icon />,
        keywords: ["h2", "heading2"],
        onSelect: () => {
          editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode("h2"))
            }
          })
        },
      }),
      new SlashCommandOption("Heading 3", {
        icon: <Heading3Icon />,
        keywords: ["h3", "heading3"],
        onSelect: () => {
          editor.update(() => {
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              $setBlocksType(selection, () => $createHeadingNode("h3"))
            }
          })
        },
      }),
    ]

    // List options
    const listOptions: SlashCommandOption[] = [
      new SlashCommandOption("Numbered list", {
        icon: <ListOrderedIcon />,
        keywords: ["numbered", "ordered", "ol", "list"],
        onSelect: () => {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        },
      }),
      new SlashCommandOption("Bullet list", {
        icon: <ListIcon />,
        keywords: ["bullet", "unordered", "ul", "list"],
        onSelect: () => {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        },
      }),
      ...(enableChecklist
        ? [
            new SlashCommandOption("Checklist", {
              icon: <ListTodoIcon />,
              keywords: ["checklist", "checkbox", "todo", "task", "check", "list"],
              onSelect: () => {
                editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
              },
            }),
          ]
        : []),
      ...(enableTable
        ? [
            new SlashCommandOption("Table", {
              icon: <TableIcon />,
              keywords: ["table"],
              onSelect: () => {
                editor.dispatchCommand(INSERT_TABLE_COMMAND, {
                  rows: "2",
                  columns: "2",
                  includeHeaders: { rows: true, columns: false },
                })
              },
            }),
          ]
        : []),
    ]

    // Media options
    const mediaOptions: SlashCommandOption[] = [
      ...(enableImages
        ? [
            new SlashCommandOption("Image", {
              icon: <ImageIcon />,
              keywords: ["image", "img", "picture", "photo"],
              onSelect: () => {
                editor.dispatchCommand(OPEN_IMAGE_DIALOG_COMMAND, undefined)
              },
            }),
          ]
        : []),
      ...(enableHtml
        ? [
            new SlashCommandOption("HTML", {
              icon: <CodeIcon />,
              keywords: ["html", "code", "embed"],
              onSelect: () => {
                editor.dispatchCommand(OPEN_RAW_HTML_DIALOG_COMMAND, {})
              },
            }),
          ]
        : []),
    ]

    const allOptions: SlashCommandOption[] = disableHeadings
      ? [...listOptions, ...mediaOptions]
      : [...headingOptions, ...listOptions, ...mediaOptions]

    return allOptions.filter((option) => {
      return option.title.toLowerCase().includes(lower) || option.keywords.some((k) => k.toLowerCase().includes(lower))
    })
  }, [editor, query, disableHeadings, enableHtml, enableImages, enableTable, enableChecklist])

  const onSelectOption = useCallback(
    (option: SlashCommandOption, nodeToRemove: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        nodeToRemove?.remove() // Remove the typed "/" text
      })
      option.onSelect(query || "")
      closeMenu()
    },
    [editor, query]
  )

  if (!menuPortal) return null

  return (
    <>
      {createPortal(<div ref={menuRef} />, menuPortal)}
      <LexicalTypeaheadMenuPlugin
        onQueryChange={setQuery}
        onSelectOption={onSelectOption}
        options={options}
        triggerFn={triggerMatch}
        menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
          requestAnimationFrame(() => requestAnimationFrame(positionMenu))

          const isOpen = !!(anchorElementRef.current && menuRef.current && options.length)

          return isOpen
            ? createPortal(
                <div
                  className="bg-background pointer-events-auto fixed isolate w-48 overflow-hidden rounded-md border shadow-md"
                  style={{ zIndex: 9999 }}
                  onClick={(e) => e.stopPropagation()}
                  onWheel={(e) => e.stopPropagation()}
                >
                  <ul className="m-0 list-none p-0">
                    {options.map((option, i: number) => (
                      <SlashCommandMenuItem
                        isSelected={selectedIndex === i}
                        onClick={() => {
                          setHighlightedIndex(i)
                          selectOptionAndCleanUp(option)
                        }}
                        onMouseEnter={() => {
                          setHighlightedIndex(i)
                        }}
                        option={option}
                        key={option.key}
                      />
                    ))}
                  </ul>
                </div>,
                menuRef.current!
              )
            : null
        }}
      />
    </>
  )
}
